import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from 'src/hooks';
import type { CookieCart, UserProfile } from 'src/types';
import { getCookie, setCookie } from 'src/utils';

/**
 * Removes a cart association: deletes it from the logged-in user, or drops it
 * from the cookie cart list for anonymous users. Mirror of
 * useSaveCartToUserOrCookie.
 */
export const useRemoveCartFromUserOrCookie = () => {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (cartId: string) => {
      const res = await fetch(`/user/cart/delete/${cartId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Error removing single cart...');
      }
      return true;
    },
    onSuccess: (_mutationReturn, cartId) => {
      queryClient.setQueryData(['currentUser'], (oldData: UserProfile) =>
        oldData
          ? { ...oldData, carts: oldData.carts.filter((c) => c !== cartId) }
          : oldData,
      );
      queryClient.invalidateQueries({
        queryKey: ['cart', 'list'],
        exact: false,
        type: 'all',
      });
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const removeCartFromUserOrCookie = (cartId: string) => {
    // no user, drop the cart from cookies
    if (user === null) {
      const cookieCarts = getCookie('cookieCarts');
      if (!cookieCarts) return;

      const carts: CookieCart[] = JSON.parse(decodeURIComponent(cookieCarts));
      const next = carts.filter((cart) => cart.cartid !== cartId);
      setCookie('cookieCarts', encodeURIComponent(JSON.stringify(next)));
    } else {
      mutation.mutate(cartId);
    }
  };

  return removeCartFromUserOrCookie;
};
